// =============================================================================
// LOADING ANIMATION - Tactical HUD Style
// =============================================================================

import { motion } from 'framer-motion';

interface LoadingAnimationProps {
  message?: string;
  streamText?: string;
}

export function LoadingAnimation({
  message = 'Обробка розвідданих...',
  streamText,
}: LoadingAnimationProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-col items-center justify-center py-16"
    >
      {/* Tactical Radar */}
      <div className="relative w-32 h-32 mb-10">
        {/* Radar Sweeper */}
        <motion.div
          className="absolute inset-0 border border-war-primary/20 rounded-full"
          initial={{ rotate: 0 }}
          animate={{ rotate: 360 }}
          transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
        >
          <div className="absolute top-0 left-1/2 w-1/2 h-0.5 bg-gradient-to-r from-war-primary to-transparent origin-left" />
        </motion.div>

        {/* Radar Circles */}
        <div className="absolute inset-4 border border-war-primary/10 rounded-full" />
        <div className="absolute inset-8 border border-war-primary/10 rounded-full" />

        {/* Central Pulse */}
        <motion.div
          className="absolute inset-14 bg-war-primary/40 rounded-full"
          animate={{ scale: [1, 1.2, 1], opacity: [0.4, 0.8, 0.4] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        />

        {/* Axis */}
        <div className="absolute top-1/2 left-0 w-full h-px bg-war-primary/5" />
        <div className="absolute left-1/2 top-0 w-px h-full bg-war-primary/5" />
      </div>

      {/* Status Text */}
      <div className="space-y-3 text-center">
        <motion.p
          className="text-war-primary font-mono text-xs md:text-sm tracking-[0.4em] uppercase font-bold"
          animate={{ opacity: [1, 0.4, 1] }}
          transition={{ duration: 1, repeat: Infinity }}
        >
          {message}
        </motion.p>
        <p className="text-war-dim font-mono text-[9px] uppercase tracking-widest">
          Neural Link: Established // Syncing...
        </p>
      </div>

      {streamText !== undefined && (
        <div className="w-full max-w-2xl mt-10 bg-war-panel/40 border border-war-dim/20 p-5 relative overflow-hidden">
          {/* Top terminal bar */}
          <div className="flex items-center justify-between mb-4 pb-2 border-b border-war-dim/10">
            <span className="text-war-primary text-[10px] font-mono tracking-widest uppercase">
              STRAT_MSG_STREAM
            </span>
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-war-primary animate-pulse" />
              <span className="text-war-dim text-[10px] font-mono uppercase">
                Live
              </span>
            </div>
          </div>

          <pre className="font-mono text-war-bright text-xs md:text-sm whitespace-pre-wrap leading-relaxed min-h-[96px] opacity-80">
            {streamText}
            <motion.span
              animate={{ opacity: [1, 0] }}
              transition={{ duration: 0.5, repeat: Infinity }}
              className="inline-block w-2 h-4 bg-war-primary ml-1 align-middle"
            />
          </pre>

          {/* Background scanline for the box */}
          <div className="absolute inset-0 pointer-events-none opacity-[0.03] bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.25)_50%),linear-gradient(90deg,rgba(255,0,0,0.06),rgba(0,255,0,0.02),rgba(0,0,255,0.06))] bg-[length:100%_2px,3px_100%]" />
        </div>
      )}
    </motion.div>
  );
}
